import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Alert, Button } from 'react-bootstrap';
import { accessToken, ensureSession, readSession, SESSION_CHANGED, SESSION_RENEWED, tokenValid } from 'helpers/api/session';

type PrivateRouteProps = {
    component: React.ComponentType;
    roles?: string;
};

const PrivateRoute = ({ component: RouteComponent, roles }: PrivateRouteProps) => {
    const location = useLocation();
    const [session, setSession] = useState(readSession);
    const [state, setState] = useState<'checking' | 'ready' | 'missing' | 'waiting'>(() =>
        !session ? 'missing' : tokenValid(accessToken(session)) ? 'ready' : 'checking');
    const [message, setMessage] = useState('');
    const [retry, setRetry] = useState(0);
    useEffect(() => {
        let mounted = true;
        const updateIdentity = (next: any) => setSession((previous: any) =>
            previous && next && previous.roleid === next.roleid && previous.user?.email === next.user?.email
                ? previous : next);
        const check = async () => {
            const current = readSession();
            if (!current) { if (mounted) { setSession(null); setState('missing'); } return; }
            try {
                const renewed = await ensureSession();
                if (mounted) { updateIdentity(renewed); setState('ready'); setMessage(''); }
            } catch (error: any) {
                // Keep already-mounted editors alive while the sign-in dialog is open.
                if (mounted) {
                    setState(previous => previous === 'ready' ? previous : 'waiting');
                    setMessage(error.message || 'Could not renew your session. Please retry.');
                }
            }
        };
        const renewed = () => { if (mounted) { updateIdentity(readSession()); setState(readSession() ? 'ready' : 'missing'); } };
        window.addEventListener(SESSION_RENEWED, renewed);
        window.addEventListener(SESSION_CHANGED, check);
        void check();
        return () => {
            mounted = false;
            window.removeEventListener(SESSION_RENEWED, renewed);
            window.removeEventListener(SESSION_CHANGED, check);
        };
    }, [retry]);

    if (state === 'missing') return <Navigate to="/account/login" state={{ from: location }} replace />;
    if (state === 'checking') return <p role="status">Checking your session…</p>;
    if (state === 'waiting') return <Alert variant="warning">{message}{' '}
        <Button variant="link" onClick={() => setRetry(value => value + 1)}>Retry</Button>
    </Alert>;
    const role = session?.roleid?.toString();
    if (roles && (!role || roles.indexOf(role) === -1)) return <Navigate to="/" replace />;
    return <RouteComponent />;
};
export default PrivateRoute;
